const SUFFIXES = ["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v", "vi"];

function stripSuffix(name: string): string {
  const lower = name.toLowerCase();
  const match = SUFFIXES.find((suffix) => lower.endsWith(` ${suffix}`));
  if (!match) return name;
  return name.slice(0, name.length - match.length - 1).trim();
}

/**
 * Splits name fields into first, middle, and last components.
 *
 * Normal case: backend sends first + middle combined in nameFirst (e.g. "John Michael"),
 * with nameLast provided separately. Suffixes (Jr, Sr, II, etc.) are stripped from
 * lastName for display.
 *
 * Fallback case: when nameLast is blank (document reader puts everything into nameFirst),
 * suffixes are stripped from nameFirst first, then the last word is used as lastName.
 * e.g. "John Michael Smith Jr" → firstName: "John", middleName: "Michael", lastName: "Smith"
 */
export function splitName(
  nameFirst: string | undefined,
  nameLast: string | undefined
): {
  firstName: string | undefined;
  middleName: string | undefined;
  lastName: string | undefined;
} {
  const firstTrimmed = nameFirst?.trim() || "";
  const lastTrimmed = nameLast?.trim() || "";

  // Normal case: nameLast is provided
  if (lastTrimmed) {
    const lastName = stripSuffix(lastTrimmed);

    if (!firstTrimmed) {
      return { firstName: undefined, middleName: undefined, lastName };
    }

    const spaceIndex = firstTrimmed.indexOf(" ");
    if (spaceIndex === -1) {
      return { firstName: firstTrimmed, middleName: undefined, lastName };
    }

    return {
      firstName: firstTrimmed.slice(0, spaceIndex),
      middleName: firstTrimmed.slice(spaceIndex + 1),
      lastName,
    };
  }

  // Fallback: nameLast is blank — extract last name from nameFirst
  if (!firstTrimmed) {
    return { firstName: undefined, middleName: undefined, lastName: undefined };
  }

  const stripped = stripSuffix(firstTrimmed);
  const parts = stripped.split(" ");

  if (parts.length === 1) {
    // Only one word — treat as first name, no last name extractable
    return { firstName: parts[0], middleName: undefined, lastName: undefined };
  }

  const lastName = parts[parts.length - 1];
  const remaining = parts.slice(0, parts.length - 1);

  return {
    firstName: remaining[0],
    middleName: remaining.length > 1 ? remaining.slice(1).join(" ") : undefined,
    lastName,
  };
}
